<template>
  <div class="relative">
    <!-- Left arrow -->
    <button
      v-if="canScrollLeft"
      @click="scroll(-1)"
      class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 hidden md:flex items-center justify-center w-9 h-9 bg-white border border-gray-200 rounded-full shadow-md hover:shadow-lg hover:border-blue-300 transition-all duration-200"
      aria-label="Scroll left"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
      </svg>
    </button>

    <!-- Right arrow -->
    <button
      v-if="canScrollRight"
      @click="scroll(1)"
      class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 hidden md:flex items-center justify-center w-9 h-9 bg-white border border-gray-200 rounded-full shadow-md hover:shadow-lg hover:border-blue-300 transition-all duration-200"
      aria-label="Scroll right"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>

    <!-- Right fade overlay (hints at more content) -->
    <div
      v-if="canScrollRight"
      class="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-gray-50 to-transparent z-[5] pointer-events-none"
    ></div>

    <!-- Left fade overlay -->
    <div
      v-if="canScrollLeft"
      class="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-gray-50 to-transparent z-[5] pointer-events-none"
    ></div>

    <!-- Scrollable strip -->
    <div
      ref="stripRef"
      class="flex gap-4 overflow-x-auto scroll-smooth pb-2"
      style="scrollbar-width: none; -ms-overflow-style: none;"
      @scroll="updateScrollState"
    >
      <!-- Destination cards -->
      <div
        v-for="(destination, index) in destinations"
        :key="index"
        @click="$emit('select', destination, index)"
        class="flex-shrink-0 w-44 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer group transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
      >
        <!-- Image -->
        <div class="h-32 overflow-hidden bg-gray-100 relative">
          <div v-if="!destination._imgLoaded" class="absolute inset-0 animate-pulse bg-gray-200"></div>
          <img
            v-if="destination.image && destination.image.url"
            :src="getOptimizedImage(destination.image.url)"
            :srcset="getOptimizedSrcset(destination.image.url)"
            sizes="176px"
            width="176"
            height="128"
            :alt="destination.image.description || destination.city"
            class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
            decoding="async"
            @load="destination._imgLoaded = true"
            @error="destination._imgLoaded = true"
          />
          <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
            </svg>
          </div>

          <!-- Price badge (User A) or continent badge (User B) -->
          <div class="absolute bottom-2 left-2">
            <span
              v-if="destination.price"
              class="inline-block bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm"
            >
              {{ t('destinationStrip.from') }}{{ destination.price }} {{ destination.currency }}
            </span>
            <span
              v-else-if="destination.continent"
              class="inline-block bg-white/85 backdrop-blur-sm text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full"
            >
              {{ destination.continent }}
            </span>
          </div>
        </div>

        <!-- Text content -->
        <div class="px-3 py-2.5">
          <p class="text-sm font-bold text-gray-800 truncate group-hover:text-blue-600 transition-colors leading-tight">
            {{ destination.city }}
          </p>
          <p class="text-xs text-gray-400 truncate mt-0.5">{{ destination.country }}</p>
        </div>
      </div>

      <!-- Explore CTA card -->
      <div
        @click="$emit('explore')"
        class="flex-shrink-0 w-44 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl shadow-sm border border-blue-500 overflow-hidden cursor-pointer group transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
      >
        <!-- Mini thumbnails grid -->
        <div class="h-32 p-2 grid grid-cols-2 gap-1">
          <div
            v-for="(preview, pi) in previewImages"
            :key="pi"
            class="rounded-lg overflow-hidden bg-white/10"
          >
            <img
              v-if="preview.image && preview.image.url"
              :src="getOptimizedImage(preview.image.url, 80)"
              :alt="preview.city"
              class="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
              loading="lazy"
            />
            <div v-else class="w-full h-full bg-white/10"></div>
          </div>
          <!-- Fill remaining slots if fewer than 4 preview images -->
          <div
            v-for="i in Math.max(0, 4 - previewImages.length)"
            :key="'empty-' + i"
            class="rounded-lg bg-white/10"
          ></div>
        </div>

        <!-- CTA text -->
        <div class="px-3 py-2.5">
          <p class="text-xs font-bold text-white leading-tight">{{ t('destinationStrip.exploreAll') }}</p>
          <p class="text-xs text-blue-200 mt-0.5 flex items-center gap-1">
            {{ t('destinationStrip.destinations') }}
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  destinations: {
    type: Array,
    default: () => []
  }
});

defineEmits(['select', 'explore']);

const stripRef = ref(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

// Last 4 destinations used as mini previews inside the Explore card
const previewImages = computed(() => {
  const len = props.destinations.length;
  return props.destinations.slice(Math.max(0, len - 4));
});

const getOptimizedImage = (url, width = 176) => {
  if (!url || !url.includes('images.unsplash.com')) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('w', String(width));
    u.searchParams.set('q', '50');
    u.searchParams.set('auto', 'format');
    u.searchParams.set('fit', 'crop');
    return u.toString();
  } catch {
    return url;
  }
};

const getOptimizedSrcset = (url) => {
  if (!url || !url.includes('images.unsplash.com')) return undefined;
  return [176, 264].map(w => `${getOptimizedImage(url, w)} ${w}w`).join(', ');
};

const updateScrollState = () => {
  const el = stripRef.value;
  if (!el) return;
  canScrollLeft.value = el.scrollLeft > 4;
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
};

const CARD_WIDTH = 192; // 176px card + 16px gap

const scroll = (direction) => {
  const el = stripRef.value;
  if (!el) return;
  el.scrollBy({ left: direction * CARD_WIDTH * 2, behavior: 'smooth' });
};

onMounted(async () => {
  await nextTick();
  updateScrollState();
  // Auto-scroll hint: wait 2 s then nudge 2 cards right, so user notices there's more
  if (stripRef.value && stripRef.value.scrollWidth > stripRef.value.clientWidth) {
    setTimeout(() => {
      stripRef.value?.scrollBy({ left: CARD_WIDTH * 2, behavior: 'smooth' });
      setTimeout(() => updateScrollState(), 600);
    }, 2000);
  }
});
</script>

<style scoped>
/* Hide scrollbar cross-browser */
.overflow-x-auto::-webkit-scrollbar {
  display: none;
}
</style>
